import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { Link } from 'react-router-dom'

/**
 * Gumbi (CLAUDE.md §17, docs/shuttly-mockup.html). Boje dolaze isključivo iz
 * tokena — nijedna heksadekadska vrijednost ne stoji u komponenti. Dodirna meta
 * je najmanje 44px (`touch-target`), jer je ovo mobile-first sučelje.
 */
export type Izgled = 'primary' | 'gold' | 'line' | 'ghost' | 'danger'

const IZGLEDI: Record<Izgled, string> = {
  primary: 'bg-noir text-ivory hover:bg-surface',
  gold: 'bg-brass text-noir font-semibold hover:bg-champagne',
  line: 'bg-transparent text-ink border border-hair hover:border-brass hover:text-brass',
  ghost: 'bg-transparent text-ink2 hover:text-ink',
  danger: 'bg-transparent text-bad-tx border border-bad-tx/40 hover:bg-bad-bg',
}

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  izgled?: Izgled
  children: ReactNode
}

const OSNOVNI = [
  'touch-target inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2',
  'text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50',
].join(' ')

/**
 * GUMB KOJI NE ŠALJE OBRAZAC DOK MU SE NE KAŽE (D31, 25.08.2026.).
 *
 * Nativni `<button>` unutar `<form>`-a je `type="submit"`, pa je SVAKI gumb u
 * `FormModal`u dotad tiho slao obrazac: u editoru informacija klik na "Dodaj
 * gumb" spremao je temu i zatvarao dijalog, a izbor toka pojavio bi se tek
 * ponovnim "Uredi". Isto je vrijedilo za "Ukloni", "Prevedi" i tri gumba
 * albuma — ondje je spašavao samo izričit `type="button"` na mjestu poziva, a
 * obrana koja se piše na svakom pozivu jednom se zaboravi.
 *
 * Zato je zadano `button`, a slanje je IZRIČITO (`type="submit"` na "Spremi",
 * `FormModal`). Isti račun na jednom mjestu (CLAUDE.md §16a): novi gumb u
 * obrascu je od sada bezopasan dok se ne izjasni.
 */
export function Button({
  izgled = 'primary',
  type = 'button',
  className = '',
  children,
  ...ostalo
}: Props) {
  return (
    <button type={type} className={[OSNOVNI, IZGLEDI[izgled], className].join(' ')} {...ostalo}>
      {children}
    </button>
  )
}

/**
 * Ista tipka, ali NAVIGACIJA. Odvojeno od `Button`a jer se razlikuje u onome što
 * korisnik od nje očekuje: poveznica se otvara u novoj kartici srednjim klikom i
 * ima adresu koju se može kopirati — `<button onClick={navigate}>` ne može ni
 * jedno ni drugo. Isti par postoji i u noir kompletu (`Gumb`/`GumbLink`).
 */
export function ButtonLink({
  na,
  izgled = 'primary',
  className = '',
  ariaCurrent,
  children,
}: {
  na: string
  izgled?: Izgled
  className?: string
  /**
   * Ekran na kojem se STOJI, kad poveznice stoje u nizu (`KatastarNav`). Izgled
   * to već kaže oku; `aria-current` je isti podatak za čitač ekrana, a nizu od
   * tri poveznice bez njega nedostaje jedina razlika koja išta znači.
   */
  ariaCurrent?: 'page'
  children: ReactNode
}) {
  return (
    <Link to={na} aria-current={ariaCurrent} className={[OSNOVNI, IZGLEDI[izgled], className].join(' ')}>
      {children}
    </Link>
  )
}
